import {
  QuoteContext,
  type RewriteOption,
  useAIGenerator,
  useOrganization,
  useToast,
} from '@airship-platform/airship-patterns';
import { EmptyState, FlexBox } from '@airship-platform/jam-web/components';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { type ControllerRenderProps } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import ROUTE_PATHS from '../../routes/paths';
import FormProposalOptionSummary, {
  type FormProposalOptionSummaryInputs,
} from './FormProposalOptionSummary';

const EditProposalOptionSummaryPage: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { currentOption, isLoading, updateAppointmentProposalOption } =
    useAppointmentProposalOptions();
  const { isLoadingOption: isGenerating, generateQuote } = useAIGenerator();
  const [previousSummary, setPreviousSummary] = useState<string | null>(null);

  const improveOptionSummaryHandler = async (
    field: ControllerRenderProps<FormProposalOptionSummaryInputs, 'summary'>,
    rewriteOption: RewriteOption
  ) => {
    if (!currentOption || !organization) return;

    const summary = field.value || '';

    const result = await generateQuote(
      summary,
      rewriteOption,
      QuoteContext.PROPOSAL_OPTION_SUMMARY,
      {
        organizationId: organization.organizationId,
        appointmentId: currentOption.appointmentId,
        appointmentProposalId: currentOption.appointmentProposalId,
        appointmentProposalOptionId: currentOption.appointmentProposalOptionId,
      }
    );

    if (result.length) {
      setPreviousSummary(summary);
      field.onChange(result.map((r) => r.improvedQuote).join(' '));
      showToast('Your option summary has been improved!', 'success');
    } else {
      showToast('We were unable to generate an improved summary', 'warning');
    }
  };

  const handleUndo = (
    field: ControllerRenderProps<FormProposalOptionSummaryInputs, 'summary'>
  ) => {
    if (previousSummary !== null) {
      field.onChange(previousSummary);
      setPreviousSummary(null);
      showToast('Update reverted', 'info');
    }
  };

  if (!currentOption) {
    return (
      <EmptyState
        title="Appointment proposal option not found"
        subtitle="The requested appointment proposal option could not be located or is unavailable for this appointment."
      />
    );
  }

  const isReadOnly = currentOption.isReadOnly;

  const handleSubmit = (data: FormProposalOptionSummaryInputs) => {
    updateAppointmentProposalOption(
      currentOption.appointmentId,
      currentOption.appointmentProposalId,
      currentOption.appointmentProposalOptionId,
      {
        summary: data.summary,
        summaryTextPlain: data.summaryTextPlain,
      }
    ).then(() => {
      navigate(
        ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
          appointmentId: currentOption.appointmentId,
          appointmentProposalId: currentOption.appointmentProposalId,
          appointmentProposalOptionId:
            currentOption.appointmentProposalOptionId,
        })
      );
      showToast(
        'Appointment proposal option summary updated successfully!',
        'success'
      );
    });
  };

  return (
    <FlexBox flexDirection={'column'} gap={2} p={2}>
      <Typography variant="h4">Appointment Proposal Option Summary</Typography>
      <FormProposalOptionSummary
        isLoading={isLoading}
        isDisabled={isReadOnly}
        isGenerating={isGenerating ?? null}
        previousSummary={previousSummary}
        defaultValues={{
          summary: currentOption.summary || '',
          summaryTextPlain: currentOption.summaryTextPlain || '',
        }}
        currentOption={currentOption}
        onSubmit={handleSubmit}
        onAIGenerate={improveOptionSummaryHandler}
        onUndo={handleUndo}
      />
    </FlexBox>
  );
};

export default EditProposalOptionSummaryPage;
